import React, { useEffect, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRotateRight,
  faTriangleExclamation,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { enviroment } from "../../../enviroment";
import {
  SupplierStatusView,
  useSupplierStatus,
} from "../../../hooks/useSupplierStatus";
import { useMinuteTick } from "../../../hooks/useMinuteTick";
import { SupplierStatus } from "../../../core/models/entities/SupplierStatus";
import {
  supplierStatusEpisodeKey,
  supplierStatusSinceAge,
} from "../../../utils/functions/supplierStatusPoll";
import { trackSupplierDegradedShown } from "../../../utils/functions/analytics";

// Flag REACT_APP_SUPPLIER_STATUS_ENABLED - build-time constant (see
// useSupplierStatus): with the flag off the export is a component that
// renders null, so the DOM is byte-identical.
const statusEnabled = enviroment.supplierStatusEnabled;

// Per-session dismissal: the episode key (reason + since) the user closed.
// sessionStorage so an F5 keeps the choice for this tab, a new episode (new
// key) shows the banner again. Unavailable storage = in-memory only.
export const SUPPLIER_STATUS_DISMISSED_KEY = "kitmaker.supplierStatus.dismissed";

function readDismissed(): string | null {
  try {
    return window.sessionStorage.getItem(SUPPLIER_STATUS_DISMISSED_KEY);
  } catch {
    return null;
  }
}

function writeDismissed(key: string): void {
  try {
    window.sessionStorage.setItem(SUPPLIER_STATUS_DISMISSED_KEY, key);
  } catch {
    // sessionStorage unavailable - the in-memory state still hides it.
  }
}

// Clarity event leverandoer_nede_vist (flag REACT_APP_CLARITY_EVENTS_ENABLED,
// gated in the facade like the other events): ONE per episode per tab
// session. The banner is mounted once in the app shell, so a route change
// does not re-fire; the episode key in sessionStorage keeps an F5 from
// counting the same outage twice. Flag off = nothing is written or sent.
export const SUPPLIER_STATUS_EVENT_SENT_KEY = "kitmaker.supplierStatus.eventSent";

function readEventSent(): string | null {
  try {
    return window.sessionStorage.getItem(SUPPLIER_STATUS_EVENT_SENT_KEY);
  } catch {
    return null;
  }
}

function writeEventSent(key: string): void {
  try {
    window.sessionStorage.setItem(SUPPLIER_STATUS_EVENT_SENT_KEY, key);
  } catch {
    // sessionStorage unavailable - the in-memory guard below still holds.
  }
}

function useSupplierDegradedShownEvent(episode: string): void {
  const sentRef = useRef<string | null>(null);
  useEffect(() => {
    if (!enviroment.clarityEventsEnabled) return;
    if (sentRef.current === episode || readEventSent() === episode) return;
    sentRef.current = episode;
    writeEventSent(episode);
    trackSupplierDegradedShown();
  }, [episode]);
}

/** "m:ss" for the countdown; whole seconds, rounded up, never negative. */
export function formatCountdown(remainingMs: number): string {
  const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

// One-second clock for the countdown and the "Check now" lock, running
// ONLY while `active` (a countdown or a lock to show) and only while the
// banner is mounted - so a healthy page pays nothing. Returns nothing: the
// caller reads Date.now() at render, the tick just re-renders it.
function useSecondTick(active: boolean): void {
  const [, tick] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (!active) return undefined;
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [active]);
}

/**
 * Amber, non-blocking bar at the top of the page (under the fixed header,
 * above the routed screen) while the logo supplier is degraded: "Reduced
 * functionality: the logo supplier is not responding right now…" with the
 * episode's relative start ("Started 12 min ago"), a live countdown to the
 * next automatic read ("Next check in 6:59", paused while the tab is
 * hidden, "Checking…" while a read is in flight), a "Check now" button
 * (one extra read, locked 15 s) and a dismiss per session (comes back on
 * the next state change). Renders nothing while ok, before the first
 * answer, on a read error, or after the route answered 404.
 */
function SupplierStatusBannerEnabled() {
  const view = useSupplierStatus();
  const { status } = view;
  const [dismissed, setDismissed] = useState<string | null>(readDismissed);
  if (!status || !status.degraded) return null;
  const episode = supplierStatusEpisodeKey(status);
  if (dismissed === episode) return null;
  return (
    <SupplierStatusBannerBody
      status={status}
      view={view}
      onDismiss={() => {
        writeDismissed(episode);
        setDismissed(episode);
      }}
    />
  );
}

// The visible bar: mounted only while degraded and not dismissed, so the
// shared minute clock (one interval for the page) and the second clock run
// only then.
function SupplierStatusBannerBody({
  status,
  view,
  onDismiss,
}: {
  status: SupplierStatus;
  view: SupplierStatusView;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  useMinuteTick();
  useSupplierDegradedShownEvent(supplierStatusEpisodeKey(status));
  const { nextCheckAt, checking, checkNow, checkNowLockedUntil } = view;
  const now = Date.now();
  const locked = now < checkNowLockedUntil;
  useSecondTick(nextCheckAt !== null || locked);
  const nextCheckText = checking
    ? t("supplier_status_checking")
    : nextCheckAt !== null
      ? t("supplier_status_next_check", {
          time: formatCountdown(nextCheckAt - now),
        })
      : t("supplier_status_paused");
  const age = supplierStatusSinceAge(status.since);
  const sinceText =
    age === null
      ? null
      : age.unit === "justNow"
        ? t("supplier_status_since_just_now")
        : age.unit === "minutes"
          ? t("supplier_status_since_minutes", { minutes: age.value })
          : t("supplier_status_since_hours", { hours: age.value });
  return (
    <div
      role="status"
      data-testid="supplier-status-banner"
      className="w-full flex items-start gap-x-3 bg-amber-100 border border-amber-300 text-amber-900 px-4 py-2 mb-4 rounded-md text-sm"
    >
      <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5" />
      <div
        className="flex-1"
        {...(enviroment.clarityUx2Enabled
          ? // Dead clicks round 2: 28 clicks on the banner text opened
            // nothing. The text stays deliberately non-interactive; a title
            // says what to do instead ("Check now"). On the text container,
            // not the bar, so the buttons do not inherit "use Check now".
            { title: t("ux2.supplierBannerHint") }
          : {})}
      >
        <span>{t("supplier_status_banner")}</span>
        {sinceText && (
          <span
            className="ml-2 whitespace-nowrap text-amber-800/80"
            data-testid="supplier-status-since"
          >
            {sinceText}
          </span>
        )}
        <span
          className="ml-2 whitespace-nowrap text-amber-800/80"
          data-testid="supplier-status-next-check"
          aria-live="off"
        >
          {nextCheckText}
        </span>
      </div>
      <button
        type="button"
        onClick={checkNow}
        disabled={checking || locked}
        data-testid="supplier-status-check-now"
        className="flex items-center gap-x-1 whitespace-nowrap rounded-full border border-amber-400 px-2 py-0.5 text-xs font-semibold hover:bg-amber-200 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
      >
        <FontAwesomeIcon icon={faRotateRight} spin={checking} />
        {t("supplier_status_check_now")}
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t("supplier_status_dismiss")}
        className="flex items-center rounded-full border border-amber-400 px-2 py-0.5 text-xs font-semibold hover:bg-amber-200"
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>
    </div>
  );
}

// Flag off: renders nothing and runs no hook (byte-identical DOM, 0 calls).
function SupplierStatusBannerDisabled() {
  return null;
}

export const SupplierStatusBanner: () => JSX.Element | null = statusEnabled
  ? SupplierStatusBannerEnabled
  : SupplierStatusBannerDisabled;
